import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import adminApi from "../../../api/adminApi";
import { useFilesContext } from "./FilesContext";

interface Props {
  catalogueIds: string[];
  description: string;
  open: boolean;
  onCancel: () => void;
  onSubmit: (catalogueIds: string[]) => void;
}

const deleteFiles = withErrorHandling(adminApi.deleteFiles);
const detachEntityFiles = withErrorHandling(adminApi.detachEntityFiles);

const DeleteFilesDialog = ({ catalogueIds, description, open, onCancel, onSubmit }: Props) => {
  const { entityInfo } = useFilesContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [deleting, setDeleting] = useState(false);

  const title = catalogueIds.length === 1 ? `Delete file?` : "Delete files?";

  const handleDelete = async () => {
    if (catalogueIds.length === 0) {
      return;
    }

    setDeleting(true);

    const [resp, error] = entityInfo
      ? await detachEntityFiles(entityInfo.entityType, entityInfo.entityId, entityInfo.objectType, catalogueIds)
      : await deleteFiles(catalogueIds);

    if (error || !resp.result) {
      sendNotificationError(`Failed to delete ${description}`);
    } else {
      sendNotification(`${description} deleted successfully`);
      onSubmit(catalogueIds);
    }

    setDeleting(false);
  };

  return (
    <SmallDialog open={open} onClose={onCancel}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onCancel} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">{title}</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{description}</strong>
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onCancel}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteFilesDialog;
